import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Form, FormFeedback, Input, Label, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "auth/useAuth";
import { translateAuthError, normalizeEmail } from "auth/cognitoClient";
import { getPendingReset, clearPendingReset } from "auth/pendingState";
import { track, EVENTS } from "telemetry/track";
import AuthLayout from "./AuthLayout";

const PASSWORD_RULES = "Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e símbolo.";
const RESEND_COOLDOWN_S = 60;

/**
 * Code + new password step of the recovery flow. Like /confirmar-conta, it
 * resumes from localStorage if the tab was reloaded while the user fetched
 * the code from their e-mail app.
 */
const ResetPassword = () => {
  document.title = "Redefinir senha | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pending = useMemo(() => getPendingReset(), []);
  const email = normalizeEmail(location.state?.email || pending?.email || "");
  const resumed = !location.state?.email && !!pending?.email;

  const [submitError, setSubmitError] = useState(null);
  const [info, setInfo] = useState(
    location.state?.justRequested ? `Se existir uma conta para ${email}, um código de 6 dígitos foi enviado. Confira também a caixa de spam.` : null
  );
  const [cooldown, setCooldown] = useState(location.state?.justRequested ? RESEND_COOLDOWN_S : 0);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const form = useFormik({
    initialValues: { code: "", password: "", confirm: "" },
    validationSchema: Yup.object({
      code: Yup.string().matches(/^\d{6}$/, "O código tem 6 números.").required("Digite o código recebido."),
      password: Yup.string()
        .min(8, PASSWORD_RULES)
        .matches(/[a-z]/, PASSWORD_RULES)
        .matches(/[A-Z]/, PASSWORD_RULES)
        .matches(/\d/, PASSWORD_RULES)
        .matches(/[^A-Za-z0-9]/, PASSWORD_RULES)
        .required("Crie uma nova senha."),
      confirm: Yup.string().oneOf([Yup.ref("password")], "As senhas não coincidem.").required("Confirme a nova senha."),
    }),
    onSubmit: async ({ code, password }) => {
      setSubmitError(null);
      try {
        await auth.confirmForgotPassword(email, code, password);
        track(EVENTS.RESET_COMPLETED);
        navigate("/login", { replace: true, state: { email, flash: "Senha redefinida! Entre com a nova senha." } });
      } catch (err) {
        console.error("Reset password failed:", err);
        setSubmitError(translateAuthError(err));
      }
    },
  });

  const handleResend = async () => {
    if (!email || cooldown > 0) return;
    setResending(true);
    setSubmitError(null);
    try {
      await auth.forgotPassword(email);
      setInfo(`Novo código enviado para ${email}.`);
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      console.error("Resend reset code failed:", err);
      setSubmitError(translateAuthError(err));
    } finally {
      setResending(false);
    }
  };

  const handleStartOver = () => {
    clearPendingReset();
    navigate("/esqueci-senha", { replace: true });
  };

  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to="/" replace />;
  if (!email) return <Navigate to="/esqueci-senha" replace />;

  return (
    <AuthLayout
      title="Crie sua nova senha"
      subtitle="Digite o código recebido e escolha a nova senha."
      footer={
        <p className="mb-0">
          <Link to="/login" state={{ email }} className="fw-medium text-primary">Voltar para entrar</Link>
        </p>
      }
    >
      {resumed && (
        <Alert color="info" className="small">
          Você já pediu um código para este e-mail. Se ainda o tem, é só digitar abaixo. Se não, peça um novo.
        </Alert>
      )}
      {info && <Alert color="success">{info}</Alert>}
      {submitError && <Alert color="danger">{submitError}</Alert>}

      <p className="text-muted mb-3">
        Conta: <strong>{email}</strong>.{" "}
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={handleStartOver}>
          Trocar e-mail
        </button>
      </p>

      <Form onSubmit={form.handleSubmit} noValidate>
        <div className="mb-3">
          <Label htmlFor="rp-code">Código recebido por e-mail</Label>
          <Input
            id="rp-code" name="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
            autoComplete="one-time-code" placeholder="000000" autoFocus
            className="form-control-lg text-center fw-semibold" style={{ letterSpacing: "0.4em" }}
            value={form.values.code}
            onChange={(e) => form.setFieldValue("code", e.target.value.replace(/\D/g, "").slice(0, 6))}
            onBlur={form.handleBlur}
            invalid={!!(form.touched.code && form.errors.code)}
          />
          <FormFeedback>{form.errors.code}</FormFeedback>
          <div className="small mt-1">
            <span className="text-muted">Não recebeu? </span>
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={handleResend}
              disabled={cooldown > 0 || resending}>
              {resending ? "Enviando..." : cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <Label htmlFor="rp-password">Nova senha</Label>
          <div className="input-group auth-pass-inputgroup">
            <Input id="rp-password" name="password" type={showPassword ? "text" : "password"} autoComplete="new-password"
              placeholder="Crie uma nova senha"
              value={form.values.password} onChange={form.handleChange} onBlur={form.handleBlur}
              invalid={!!(form.touched.password && form.errors.password)} />
            <button type="button" className="btn btn-light" onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>
              <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`} />
            </button>
            <FormFeedback>{form.errors.password}</FormFeedback>
          </div>
          {!(form.touched.password && form.errors.password) && <div className="form-text">{PASSWORD_RULES}</div>}
        </div>

        <div className="mb-3">
          <Label htmlFor="rp-confirm">Confirmar nova senha</Label>
          <Input id="rp-confirm" name="confirm" type={showPassword ? "text" : "password"} autoComplete="new-password"
            placeholder="Repita a nova senha"
            value={form.values.confirm} onChange={form.handleChange} onBlur={form.handleBlur}
            invalid={!!(form.touched.confirm && form.errors.confirm)} />
          <FormFeedback>{form.errors.confirm}</FormFeedback>
        </div>

        <div className="d-grid">
          <Button color="primary" type="submit" disabled={form.isSubmitting}>
            {form.isSubmitting && <Spinner size="sm" className="me-2" />}
            Salvar nova senha
          </Button>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default ResetPassword;
